import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Switch,
  Select,
  Tabs,
  Radio,
  Typography,
  Spin,
  message,
  Tooltip,
  Statistic
} from 'antd';
import { Line } from '@ant-design/charts';
import {
  ThunderboltOutlined,
  RocketOutlined,
  CarOutlined,
  ClockCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

// 类型定义
interface GasQuote {
  level: '极速' | '快速' | '正常' | '缓慢';
  priorityFeeGwei: string;
  maxFeeGwei: string;
  estimatedTimeSec: number;
  costUSD: string;
  icon: React.ReactNode;
  color: string;
}

interface RealtimeData {
  ethPriceUSD: number | null;
  ethPriceCNY: number | null;
  baseFeeGwei: string | null;
  gasQuotes: GasQuote[];
}

interface HistoricalData {
  heatmap: Record<string, Record<string, number>> | null;
  chart: Array<{ timestamp: number; value: number; }> | null;
}

interface UISettings {
  isEip1559: boolean;
  currency: 'USD' | 'CNY';
  chartTimeRange: '1D' | '7D' | '1M' | 'All';
}

// API配置
const ALCHEMY_API_KEY = 'FL6BaqcJywosW4svjdO4oje47zbP45Yq';
const ALCHEMY_RPC_URL = `https://eth-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`;
const REWARD_PERCENTILES = [10, 30, 50, 90];

const EthGasTracker: React.FC = () => {
  const [realtimeData, setRealtimeData] = useState<RealtimeData>({
    ethPriceUSD: null,
    ethPriceCNY: null,
    baseFeeGwei: null,
    gasQuotes: []
  });

  const [historicalData, setHistoricalData] = useState<HistoricalData>({
    heatmap: null,
    chart: null
  });

  const [uiSettings, setUISettings] = useState<UISettings>({
    isEip1559: true,
    currency: 'USD',
    chartTimeRange: '7D'
  });

  const [isLoading, setIsLoading] = useState({
    realtime: true,
    historical: true
  });

  // 格式化时间
  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}秒`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) return `${minutes}分钟`;
    return `${minutes}分${remainingSeconds}秒`;
  };

  const toGwei = (hexValue?: string): number => {
    if (!hexValue) return 0;
    try {
      return Number(BigInt(hexValue)) / 1e9;
    } catch (error) {
      console.error('解析Gas数值失败:', error);
      return 0;
    }
  };

  const normalizeGwei = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    return Math.max(value, 0);
  };

  const formatGwei = (value: number): string => normalizeGwei(value).toFixed(2);

  // 获取ETH价格
  const fetchETHPrice = async (): Promise<{ usd: number; cny: number }> => {
    try {
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd,cny');
      const data = await response.json();
      return {
        usd: data.ethereum.usd,
        cny: data.ethereum.cny
      };
    } catch (error) {
      console.error('获取ETH价格失败:', error);
      return { usd: 3000, cny: 21000 }; // 默认价格
    }
  };

  // 获取实时Gas数据
  const fetchRealtimeGasData = useCallback(async () => {
    try {
      setIsLoading(prev => ({ ...prev, realtime: true }));

      // 并发获取数据
      const [ethPrices, feeHistoryResponse, gasPriceResponse] = await Promise.all([
        fetchETHPrice(),
        fetch(ALCHEMY_RPC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'eth_feeHistory',
            params: ['0x5', 'pending', REWARD_PERCENTILES],
            id: 1
          })
        }),
        fetch(ALCHEMY_RPC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'eth_gasPrice',
            params: [],
            id: 2
          })
        })
      ]);

      const feeHistoryData = await feeHistoryResponse.json();
      const gasPriceData = await gasPriceResponse.json();

      const baseFeeList = feeHistoryData?.result?.baseFeePerGas || [];
      const baseFeeHex = baseFeeList[baseFeeList.length - 1] || '0x0';
      const baseFeeGweiValue = toGwei(baseFeeHex);
      const baseFeeGwei = formatGwei(baseFeeGweiValue);

      const rewardList: string[][] = feeHistoryData?.result?.reward || [];
      const priorityByPercentile = REWARD_PERCENTILES.map((_, index) => {
        const values = rewardList
          .map(reward => reward?.[index])
          .filter(Boolean)
          .map(value => toGwei(value));
        if (values.length === 0) return 0;
        const total = values.reduce((sum, value) => sum + value, 0);
        return total / values.length;
      });

      const gasPriceGwei = toGwei(gasPriceData?.result);
      const fallbackPriority = normalizeGwei(gasPriceGwei - baseFeeGweiValue);
      const fallbackPriorityLevels = [
        fallbackPriority * 0.8,
        fallbackPriority,
        fallbackPriority * 1.2,
        fallbackPriority * 1.5
      ];
      const priorityLevels = priorityByPercentile.some(value => value > 0)
        ? priorityByPercentile
        : fallbackPriorityLevels;

      // 构建Gas报价数据
      const gasQuotes: GasQuote[] = [
        {
          level: '极速',
          priorityFeeGwei: formatGwei(priorityLevels[3]),
          maxFeeGwei: formatGwei(baseFeeGweiValue + priorityLevels[3]),
          estimatedTimeSec: 15,
          costUSD: '',
          icon: <ThunderboltOutlined />,
          color: '#ff4d4f'
        },
        {
          level: '快速',
          priorityFeeGwei: formatGwei(priorityLevels[2]),
          maxFeeGwei: formatGwei(baseFeeGweiValue + priorityLevels[2]),
          estimatedTimeSec: 60,
          costUSD: '',
          icon: <RocketOutlined />,
          color: '#fa8c16'
        },
        {
          level: '正常',
          priorityFeeGwei: formatGwei(priorityLevels[1]),
          maxFeeGwei: formatGwei(baseFeeGweiValue + priorityLevels[1]),
          estimatedTimeSec: 180,
          costUSD: '',
          icon: <CarOutlined />,
          color: '#52c41a'
        },
        {
          level: '缓慢',
          priorityFeeGwei: formatGwei(priorityLevels[0]),
          maxFeeGwei: formatGwei(baseFeeGweiValue + priorityLevels[0]),
          estimatedTimeSec: 600,
          costUSD: '',
          icon: <ClockCircleOutlined />,
          color: '#1890ff'
        }
      ];

      // 计算预估成本（标准转账21000 gas）
      const gasLimit = 21000;
      const ethPrice = uiSettings.currency === 'USD' ? ethPrices.usd : ethPrices.cny;
      const currencySymbol = uiSettings.currency === 'USD' ? '$' : '¥';
      
      gasQuotes.forEach(quote => {
        const totalFeeGwei = parseFloat(quote.maxFeeGwei);
        const costETH = (totalFeeGwei * gasLimit) / 1e9;
        const cost = (costETH * ethPrice).toFixed(2);
        quote.costUSD = `${currencySymbol}${cost}`;
      });

      setRealtimeData({
        ethPriceUSD: ethPrices.usd,
        ethPriceCNY: ethPrices.cny,
        baseFeeGwei: baseFeeGwei,
        gasQuotes
      });

    } catch (error) {
      console.error('获取实时Gas数据失败:', error);
      message.error('获取实时Gas数据失败');
    } finally {
      setIsLoading(prev => ({ ...prev, realtime: false }));
    }
  }, [uiSettings.currency]);

  // 获取历史Gas数据
  const fetchHistoricalGasData = useCallback(async () => {
    try {
      setIsLoading(prev => ({ ...prev, historical: true }));

      // 尝试获取真实的历史Gas数据
      // 注意：目前免费API的限制，暂时不显示历史数据
      // 待后端实现Gas数据收集后，可以调用自建API
      
             // TODO: 替换为自建API
       // 使用方式示例：
       // import { gasHistoricalAPI } from './gasApiInterface';
       // const result = await gasHistoricalAPI.getHistoricalData(uiSettings.chartTimeRange);
       // if (result.success) {
       //   setHistoricalData({
       //     heatmap: result.data.heatmapData,
       //     chart: result.data.chartData
       //   });
       // }
      
      // 现在暂时设置为null，不显示不准确的数据
      setHistoricalData({
        heatmap: null,
        chart: null
      });

      console.log('历史Gas数据功能暂时禁用，等待后端数据源就绪');

    } catch (error) {
      console.error('获取历史Gas数据失败:', error);
      message.error('获取历史Gas数据失败');
      
      // 失败时也设置为null
      setHistoricalData({
        heatmap: null,
        chart: null
      });
    } finally {
      setIsLoading(prev => ({ ...prev, historical: false }));
    }
  }, [uiSettings.chartTimeRange]);

  // 初始化数据
  useEffect(() => {
    Promise.all([
      fetchRealtimeGasData(),
      fetchHistoricalGasData()
    ]);
  }, [fetchRealtimeGasData, fetchHistoricalGasData]);

  // 设置定时器自动刷新实时数据
  useEffect(() => {
    const interval = setInterval(fetchRealtimeGasData, 15000); // 每15秒刷新一次
    return () => clearInterval(interval);
  }, [fetchRealtimeGasData]);

  // Gas卡片组件
  const GasCard: React.FC<{ data: GasQuote; isActive: boolean }> = ({ data, isActive }) => (
    <Card
      size="small"
      bordered
      style={{
        borderColor: isActive ? data.color : '#d9d9d9',
        borderWidth: isActive ? 2 : 1,
        height: '100%'
      }}
    >
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 18, color: data.color, marginRight: 8 }}>
            {data.icon}
          </span>
          <Text style={{ fontSize: 16, fontWeight: 'bold' }}>{data.level}</Text>
        </div>
        
        <div style={{ fontSize: 28, fontWeight: 'bold', color: data.color, marginBottom: 12 }}>
          {data.maxFeeGwei}
        </div>
        
        <div style={{ textAlign: 'left', fontSize: 12, color: '#666', lineHeight: 1.5 }}>
          {uiSettings.isEip1559 ? (
            <>
              <div>基础费用: {realtimeData.baseFeeGwei || '--'} Gwei</div>
              <div>优先费用: {data.priorityFeeGwei} Gwei</div>
            </>
          ) : (
            <div>Gas Price: {data.maxFeeGwei} Gwei</div>
          )}
                     <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
             <div>预估成本: {data.costUSD}</div>
             <div>预估时间: ≈ {formatTime(data.estimatedTimeSec)}</div>
           </div>
        </div>
      </div>
    </Card>
  );

  // 热力图组件
  const GasHeatmap: React.FC = () => {
    if (isLoading.historical) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">正在获取历史Gas数据...</Text>
          </div>
        </div>
      );
    }

    if (!historicalData.heatmap) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0', border: '1px dashed #d9d9d9', borderRadius: 8 }}>
          <Text type="secondary" style={{ fontSize: 16 }}>
            📊 历史Gas数据暂未开放
          </Text>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              我们正在构建自己的Gas数据收集系统，敬请期待...
            </Text>
          </div>
        </div>
      );
    }

    const days = Object.keys(historicalData.heatmap).sort();
    const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);

    // 计算颜色强度
    const allValues = Object.values(historicalData.heatmap).flatMap(day => Object.values(day));
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    const getColor = (value: number) => {
      const intensity = (value - minValue) / (maxValue - minValue);
      const alpha = 0.2 + intensity * 0.8;
      return `rgba(24, 144, 255, ${alpha})`;
    };

    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(24, 1fr)', gap: 1, fontSize: 10 }}>
          <div></div>
          {hours.map(hour => (
            <div key={hour} style={{ textAlign: 'center', padding: 2 }}>
              {hour.split(':')[0]}
            </div>
          ))}
          
          {days.map(day => (
            <React.Fragment key={day}>
              <div style={{ padding: '4px 8px', fontSize: 10, textAlign: 'right' }}>
                {day.split('-').slice(1).join('/')}
              </div>
              {hours.map(hour => {
                const value = historicalData.heatmap![day][hour] || 0;
                return (
                  <Tooltip key={`${day}-${hour}`} title={`${day} ${hour}: ${value} Gwei`}>
                    <div
                      style={{
                        backgroundColor: getColor(value),
                        border: '1px solid #e8e8e8',
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 8,
                        cursor: 'pointer'
                      }}
                    >
                      {value.toFixed(0)}
                    </div>
                  </Tooltip>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ fontSize: 12, marginRight: 8 }}>低</Text>
          <div style={{ display: 'flex', marginRight: 8 }}>
            {Array.from({ length: 5 }, (_, i) => (
              <div
                key={i}
                style={{
                  width: 20,
                  height: 12,
                  backgroundColor: `rgba(24, 144, 255, ${0.2 + i * 0.2})`,
                  border: '1px solid #e8e8e8'
                }}
              />
            ))}
          </div>
          <Text style={{ fontSize: 12 }}>高</Text>
        </div>
      </div>
    );
  };

  // 图表配置
  const chartConfig = {
    data: historicalData.chart || [],
    xField: 'timestamp',
    yField: 'value',
    xAxis: {
      type: 'time',
      tickCount: 5,
    },
    yAxis: {
      title: {
        text: 'Gas Price (Gwei)',
      },
    },
    smooth: true,
    point: false,
    line: {
      color: '#1890ff',
    },
    tooltip: {
      formatter: (datum: any) => ({
        name: 'Gas Price',
        value: `${datum.value} Gwei`,
      }),
    },
  };

  return (
    <div style={{ padding: 24 }}>
      {/* 实时Gas报价板块 */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Title level={3} style={{ margin: 0 }}>实时Gas报价</Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Select defaultValue="ethereum" style={{ width: 120 }}>
              <Option value="ethereum">Ethereum</Option>
            </Select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text>EIP-1559</Text>
              <Switch
                checked={uiSettings.isEip1559}
                onChange={(checked) => setUISettings(prev => ({ ...prev, isEip1559: checked }))}
              />
            </div>
            <Select
              value={uiSettings.currency}
              onChange={(value) => setUISettings(prev => ({ ...prev, currency: value }))}
              style={{ width: 80 }}
            >
              <Option value="USD">USD</Option>
              <Option value="CNY">CNY</Option>
            </Select>
            <Tooltip title="刷新数据">
              <ReloadOutlined
                onClick={fetchRealtimeGasData}
                style={{ fontSize: 16, cursor: 'pointer', color: '#1890ff' }}
                spin={isLoading.realtime}
              />
            </Tooltip>
          </div>
        </div>

        <Spin spinning={isLoading.realtime}>
          <Row gutter={[16, 16]}>
            {realtimeData.gasQuotes.map((quote, index) => (
              <Col xs={24} sm={12} lg={6} key={quote.level}>
                <GasCard data={quote} isActive={index === 1} />
              </Col>
            ))}
          </Row>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ETH价格: {uiSettings.currency === 'USD' 
                ? `$${realtimeData.ethPriceUSD?.toFixed(2)}` 
                : `¥${realtimeData.ethPriceCNY?.toFixed(2)}`} | 
              基础费用: {realtimeData.baseFeeGwei} Gwei | 
              数据更新于: {new Date().toLocaleTimeString()}
            </Text>
          </div>
        </Spin>
      </Card>

      {/* 历史Gas数据板块 */}
      <Card>
        <Title level={3} style={{ marginBottom: 24 }}>历史Gas数据</Title>

        <div style={{ marginBottom: 24 }}>
          <Title level={4}>历史气费热力图</Title>
          <Spin spinning={isLoading.historical}>
            <GasHeatmap />
          </Spin>
        </div>

        <Tabs
          defaultActiveKey="1"
          tabBarExtraContent={
            <Radio.Group
              value={uiSettings.chartTimeRange}
              onChange={(e) => setUISettings(prev => ({ ...prev, chartTimeRange: e.target.value }))}
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="1D">1D</Radio.Button>
              <Radio.Button value="7D">7D</Radio.Button>
              <Radio.Button value="1M">1M</Radio.Button>
              <Radio.Button value="All">All</Radio.Button>
            </Radio.Group>
          }
          items={[
            {
              key: '1',
              label: '历史价格',
              children: (
                <Spin spinning={isLoading.historical}>
                  <div style={{ height: 400 }}>
                    {historicalData.chart && historicalData.chart.length > 0 ? (
                      <Line {...chartConfig} />
                    ) : (
                      <div style={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        border: '1px dashed #d9d9d9',
                        borderRadius: 8
                      }}>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                          📈 历史趋势图表暂未开放
                        </Text>
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            我们正在构建自己的Gas数据收集系统，敬请期待...
                          </Text>
                        </div>
                      </div>
                    )}
                  </div>
                </Spin>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default EthGasTracker; 
